import { fetchWinPredictions } from "./ml_api";
import { RoundData, RoundMetadata } from "./types";

export interface TickPrediction {
  tick: number;
  t_win: number;
}

/**
 * Generates a list of ticks to request predictions for
 */
export const generatePredictionTicks = (roundData: RoundData): number[] => {
  let predictionTicks: number[] = [];

  // Get first and last ticks from frames
  if (roundData.frames.length > 0) {
    const firstTick = roundData.frames[0].tick;
    const lastTick = roundData.frames[roundData.frames.length - 1].tick;
    
    // Add regular interval ticks every 300 ticks
    for (let tick = firstTick; tick <= lastTick; tick += 300) {
      predictionTicks.push(tick);
    }
  } 

  // Add all damage ticks
  const damageTicks = roundData.damages.map(damage => damage.tick);
  predictionTicks.push(...damageTicks);

  // Add ticks where player count changes (kills)
  for (let i = 1; i < roundData.frames.length; i++) {
    const prevFrame = roundData.frames[i - 1];
    const currFrame = roundData.frames[i];

    const prevTAlive = prevFrame.t?.alivePlayers || 0;
    const prevCTAlive = prevFrame.ct?.alivePlayers || 0;
    const currTAlive = currFrame.t?.alivePlayers || 0;
    const currCTAlive = currFrame.ct?.alivePlayers || 0;

    if (prevTAlive !== currTAlive || prevCTAlive !== currCTAlive) {
      console.log("Player count changed at tick:", currFrame.tick); 
      predictionTicks.push(currFrame.tick);
    }
  }

  // Remove duplicates and sort
  predictionTicks = Array.from(new Set(predictionTicks)).sort((a, b) => a - b);

  // Remove ticks that are too close to future ticks
  predictionTicks = predictionTicks.filter((tick, index) => {
    // Keep if this is the last tick
    if (index === predictionTicks.length - 1) return true;
    
    // Remove if next tick is less than 30 ticks away
    const nextTick = predictionTicks[index + 1];
    return (nextTick - tick) >= 30;
  });

  console.log(predictionTicks)

  return predictionTicks;
};

/**
 * Fetches win predictions for a round
 */
export const fetchRoundWinPredictions = async (
  roundData: RoundData,
  roundMetadata: RoundMetadata
): Promise<{ predictions: TickPrediction[], success: boolean }> => {
  const predictionTicks = generatePredictionTicks(roundData);
  
  // If we have no ticks to predict, return empty array with success=false
  if (predictionTicks.length === 0) {
    return { predictions: [], success: false };
  }

  try {
    // Fetch predictions
    const response = await fetchWinPredictions(
      roundMetadata.match_id,
      roundMetadata.mapname,
      roundMetadata.roundnum,
      predictionTicks
    );

    // Check if all predictions are exactly 0 or 1
    const hasValidPredictions = response.ticks.some(pred => 
      pred.t_win > 0 && pred.t_win < 1
    );

    if (!hasValidPredictions) {
      console.error("All predictions are 0 or 1, indicating potential error");
      return { predictions: [], success: false };
    }
    
    return { predictions: response.ticks, success: true };
  } catch (error) {
    console.error("Failed to fetch win predictions:", error);
    return { predictions: [], success: false };
  }
};

/**
 * Gets the current win prediction for a specific tick
 */
export const getCurrentWinPrediction = (
  tick: number,
  predictions: TickPrediction[]
): { tWin: number | undefined; ctWin: number | undefined } => {
  if (!predictions || predictions.length === 0) {
    return { tWin: undefined, ctWin: undefined };
  }

  // Find predictions with ticks less than or equal to current tick
  const previousPredictions = predictions.filter(pred => pred.tick <= tick);
  
  if (previousPredictions.length === 0) {
    // If no previous predictions, use the earliest one
    const earliestPrediction = predictions.reduce(
      (earliest, current) => current.tick <= earliest.tick ? current : earliest,
      predictions[0]
    );
    return { 
      tWin: earliestPrediction.t_win, 
      ctWin: 1 - earliestPrediction.t_win 
    };
  }
  
  // Get the most recent prediction
  const mostRecentPrediction = previousPredictions.reduce(
    (latest, current) => current.tick > latest.tick ? current : latest,
    previousPredictions[0]
  );
  
  return { 
    tWin: mostRecentPrediction.t_win, 
    ctWin: 1 - mostRecentPrediction.t_win 
  };
}; 